import { logger } from '@/ui'
import { whatsAppConversationIndex, whatsAppConversationIndexQueryKey } from '@jobilla/nexus-sdk'
import type {
	WhatsAppConversationData,
	WhatsAppConversationIndexError,
	WhatsAppConversationIndexResponse,
} from '@jobilla/nexus-sdk'
import { useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

export interface WhatsAppConversationFilters {
	campaignId?: string
	needsReply?: boolean
	unread?: boolean
	page?: number
}

interface WhatsAppConversationApiParams {
	filter: {
		campaign_id?: string
		needs_reply?: number
		unread?: number
	}
	page: number
}

export type WhatsAppConversationPagination = WhatsAppConversationIndexResponse['meta']['pagination']

// The endpoint's query params (filter[...]/page) aren't declared as typed Scramble
// query params on the backend, so the generated hook/axios fn take none — build the
// params ourselves and send them through the generated axios function's AxiosRequestConfig.
const toApiParams = (filters: WhatsAppConversationFilters): WhatsAppConversationApiParams => ({
	filter: {
		...(filters.campaignId ? { campaign_id: filters.campaignId } : {}),
		...(filters.needsReply ? { needs_reply: 1 } : {}),
		...(filters.unread ? { unread: 1 } : {}),
	},
	page: filters.page ?? 1,
})

export const whatsAppConversationsQueryKey = (filters: WhatsAppConversationFilters) =>
	[...whatsAppConversationIndexQueryKey(), toApiParams(filters)] as const

const isWellFormedResponse = (body: unknown): body is WhatsAppConversationIndexResponse => {
	if (typeof body !== 'object' || body === null) {
		return false
	}
	const response = body as WhatsAppConversationIndexResponse
	return Array.isArray(response.data) && typeof response.meta === 'object' && response.meta !== null
}

export interface UseWhatsAppConversationsOptions {
	enabled?: boolean
}

export interface UseWhatsAppConversationsReturn {
	conversations: WhatsAppConversationData[]
	needsReplyCount: number
	unreadConversationsCount: number
	pagination: WhatsAppConversationPagination | undefined
	isLoading: boolean
	queryKey: ReturnType<typeof whatsAppConversationsQueryKey>
}

export function useWhatsAppConversations(
	filters: WhatsAppConversationFilters,
	options: UseWhatsAppConversationsOptions = {},
): UseWhatsAppConversationsReturn {
	const params = toApiParams(filters)
	const queryKey = whatsAppConversationsQueryKey(filters)

	const query = useQuery<WhatsAppConversationIndexResponse, AxiosError<WhatsAppConversationIndexError>>({
		queryKey,
		queryFn: ({ signal }) =>
			whatsAppConversationIndex({ signal, params }).then((response) => {
				// A proxy/CDN can answer 200 with a non-JSON body; treating it as a failed
				// fetch lets React Query keep the last good data instead of crashing the render.
				if (!isWellFormedResponse(response.data)) {
					logger.warn('GET /whatsapp/conversations returned a malformed body', {
						category: 'api',
						tags: { component: 'useWhatsAppConversations' },
						data: { receivedType: typeof response.data },
					})
					throw new Error('Malformed /whatsapp/conversations response body')
				}
				return response.data
			}),
		refetchInterval: 30_000,
		enabled: options.enabled ?? true,
	})

	return {
		conversations: query.data?.data ?? [],
		needsReplyCount: query.data?.meta?.needsReplyCount ?? 0,
		unreadConversationsCount: query.data?.meta?.unreadConversationsCount ?? 0,
		pagination: query.data?.meta?.pagination,
		isLoading: query.isLoading,
		queryKey,
	}
}
